import { useCallback, useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useCountUp } from "@/hooks/use-count-up";
import { realizacieGallery, realizacieStats } from "@/lib/realizacie-data";

function RealStat({
  value,
  suffix,
  label,
  decimals,
}: {
  value: number;
  suffix: string;
  label: string;
  decimals?: number;
}) {
  const { ref, value: current } = useCountUp<HTMLDivElement>(value, 1600, decimals ?? 0);
  const display = decimals
    ? current.toFixed(decimals).replace(".", ",")
    : Math.round(current).toString();
  return (
    <div ref={ref} className="text-center">
      <div className="flex items-baseline justify-center gap-1">
        <span className="text-5xl font-extrabold tracking-tight text-foreground sm:text-6xl">
          {display}
        </span>
        <span className="text-3xl font-extrabold text-primary-dark sm:text-4xl">{suffix}</span>
      </div>
      <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground sm:text-sm">
        {label}
      </p>
    </div>
  );
}

export function RealizacieGallery() {
  const [active, setActive] = useState<number | null>(null);

  const close = useCallback(() => setActive(null), []);
  const move = useCallback(
    (dir: number) =>
      setActive((i) =>
        i === null ? i : (i + dir + realizacieGallery.length) % realizacieGallery.length,
      ),
    [],
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, move]);

  return (
    <section id="realizacie" className="scroll-mt-24 bg-background py-20 md:py-28">
      <div className="container-artbaz">
        {/* Header */}
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="eyebrow justify-center">Portfólio</span>
          <h1 className="mt-6 text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Naše realizácie
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Každý projekt je výsledkom dôkladného plánovania, precíznej práce a dôrazu na detail.
            Pozrite si výber realizácií, ktoré sme úspešne dokončili pre našich klientov.
          </p>
        </Reveal>

        {/* Masonry gallery */}
        <div className="mt-14 columns-1 gap-5 sm:columns-2 lg:columns-3 [&>*]:mb-5">
          {realizacieGallery.map((item, i) => (
            <button
              key={item.src}
              type="button"
              onClick={() => setActive(i)}
              className="group relative block w-full cursor-pointer overflow-hidden rounded-2xl shadow-[var(--shadow-card)] transition-shadow duration-300 hover:shadow-[var(--shadow-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label={`Zväčšiť: ${item.alt}`}
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="w-full object-cover transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
              />
              <span className="pointer-events-none absolute inset-0 bg-charcoal/0 transition-colors duration-300 group-hover:bg-charcoal/15" />
            </button>
          ))}
        </div>

        {/* Trust statistics — clean white band with breathing room */}
        <Reveal className="mt-24 grid grid-cols-1 gap-12 border-t border-border pt-16 sm:grid-cols-3 sm:gap-6">
          {realizacieStats.map((stat) => (
            <RealStat key={stat.label} {...stat} />
          ))}
        </Reveal>

        {/* Green CTA — architectural panel matching the O nás geometry */}
        <Reveal className="mt-12 [filter:drop-shadow(0_24px_40px_color-mix(in_oklab,var(--primary)_28%,transparent))]">
          <div
            className="bg-gradient-to-br from-primary to-primary-dark px-7 py-20 text-center text-primary-foreground md:px-12 md:py-24"
            style={{
              clipPath:
                "polygon(0% 18%, 9% 18%, 14% 0%, 100% 0%, 100% 100%, 80% 100%, 74% 82%, 0% 82%)",
            }}
          >
            <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Máte podobný projekt?</h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-primary-foreground/85">
              Radi vám pripravíme návrh riešenia aj cenovú ponuku.
            </p>
            <a
              href="/kontakt"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-background px-8 py-3.5 text-sm font-bold text-foreground shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              Dohodnúť stretnutie
              <ArrowRight className="size-5" />
            </a>
          </div>
        </Reveal>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/95 p-4 animate-fade-in"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Galéria realizácií"
        >
          <button
            type="button"
            onClick={close}
            aria-label="Zavrieť"
            className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X className="size-6" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              move(-1);
            }}
            aria-label="Predchádzajúci obrázok"
            className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:left-8"
          >
            <ChevronLeft className="size-7" />
          </button>

          <figure className="max-h-[88vh] max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={realizacieGallery[active].src}
              alt={realizacieGallery[active].alt}
              className="mx-auto max-h-[80vh] w-auto rounded-xl object-contain"
            />
            <figcaption className="mt-4 text-center text-sm font-medium text-white/80">
              {realizacieGallery[active].alt}
              <span className="ml-2 text-white/50">
                ({active + 1} / {realizacieGallery.length})
              </span>
            </figcaption>
          </figure>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              move(1);
            }}
            aria-label="Ďalší obrázok"
            className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 md:right-8"
          >
            <ChevronRight className="size-7" />
          </button>
        </div>
      )}
    </section>
  );
}
